import type { ComponentType } from "react";
import { createRootRoute, createRoute, createRouter, redirect, Navigate, Outlet } from "@tanstack/react-router";
import { Activity, BarChartSquare02, Beaker02, BookOpen01, ClockRewind, Coins01, FileSearch02, Home02, Phone01, Settings01, User01 } from "@untitledui/icons";
import { AppShell } from "@/components/app/AppShell";
import { FeatureGate } from "@/components/app/FeatureGate";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import type { NavItemType } from "@/components/application/app-navigation/config";
import { isAuthenticated } from "@/lib/auth";
import { useConnectors } from "@/lib/connectors";
import { useFeatures, useNavVisibility, visibilityOf, isNavVisible, navVisible } from "@/lib/license";
import { TESTING_FEATURES, visibleTestingSections } from "@/pages/testing/testingNav";
import { LoginPage } from "@/pages/LoginPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { TokensPage } from "@/pages/TokensPage";
import { TokenDetailPage } from "@/pages/TokenDetailPage";
import { PaatProfilesPage } from "@/pages/PaatProfilesPage";
import { ServerStatsPage } from "@/pages/ServerStatsPage";
import { TransactionLogsPage } from "@/pages/TransactionLogsPage";
import { LogsPage } from "@/pages/LogsPage";
import { DocsPage } from "@/pages/DocsPage";
import { SettingsLayout } from "@/pages/settings/SettingsLayout";
import { SettingsOverviewPage } from "@/pages/settings/SettingsOverviewPage";
import { UsersPage } from "@/pages/settings/UsersPage";
import { ConnectorsPage } from "@/pages/settings/ConnectorsPage";
import { MaintenancePage } from "@/pages/settings/MaintenancePage";
import { NotificationsPage } from "@/pages/settings/NotificationsPage";
import { GeneralPage } from "@/pages/settings/GeneralPage";
import { TokenSettingsPage } from "@/pages/settings/TokenSettingsPage";
import { UiSettingsPage } from "@/pages/settings/UiSettingsPage";
import { AppearancePage } from "@/pages/settings/AppearancePage";
import { AuditPage } from "@/pages/settings/AuditPage";
import { TlsPage } from "@/pages/settings/TlsPage";
import { AccountPage } from "@/pages/AccountPage";
import { ApiKeysPage } from "@/pages/settings/ApiKeysPage";
import { LicensePage } from "@/pages/settings/LicensePage";
import { DirectoryPage } from "@/pages/settings/DirectoryPage";
import { TestingLayout } from "@/pages/testing/TestingLayout";
import { TestActivationPage } from "@/pages/testing/TestActivationPage";
import { TestOtpPasswordPage } from "@/pages/testing/TestOtpPasswordPage";
import { TestDeviceReplacementPage } from "@/pages/testing/TestDeviceReplacementPage";
import { TestAdminPage } from "@/pages/testing/TestAdminPage";
import { ApiToolsPage } from "@/pages/testing/ApiToolsPage";
import { SoapToolsPage } from "@/pages/testing/SoapToolsPage";
import { EChannelsToolsPage } from "@/pages/testing/EChannelsToolsPage";
import { AsymToolsPage } from "@/pages/testing/AsymToolsPage";
import { LoadtestGeneratorPage } from "@/pages/testing/LoadtestGeneratorPage";
import { ScenariosPage } from "@/pages/testing/ScenariosPage";

/**
 * Code-based TanStack Router. `basepath: "/app"` — SPA je servírované Spring Bootem
 * pod `/app/`; neznámé cesty pod `/app/**` server forwardne na SPA shell (SpaController).
 *
 * Stránky vázané na licencovanou funkci se obalují {@link FeatureGate} — zrcadlí
 * to, co backend vynucuje na `/admin/api/**`. Bez toho se stránka vykreslí
 * a teprve její dotazy spadnou na 403 bez vysvětlení.
 */
const gated = (feature: string, Page: ComponentType) => () => (
  <FeatureGate feature={feature}>
    <Page />
  </FeatureGate>
);

const rootRoute = createRootRoute({ component: () => <Outlet /> });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
  validateSearch: (s: Record<string, unknown>) => ({
    ...("error" in s ? { error: true } : {}),
    ...("logout" in s ? { logout: true } : {}),
  }),
});

/** Chráněný layout — sidebar shell + auth guard. */
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "shell",
  beforeLoad: () => {
    if (!isAuthenticated()) throw redirect({ to: "/login" });
  },
  component: ShellLayout,
});

const dashboardRoute = createRoute({ getParentRoute: () => shellRoute, path: "/", component: DashboardPage });
const tokensRoute = createRoute({ getParentRoute: () => shellRoute, path: "/tokens", component: gated("core.token.list", TokensPage) });
const tokenDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/tokens/$tokenId",
  component: gated("core.token.detail", TokenDetailPage),
});
const paatRoute = createRoute({ getParentRoute: () => shellRoute, path: "/paat", component: PaatProfilesPage });
const serverStatsRoute = createRoute({ getParentRoute: () => shellRoute, path: "/server-stats", component: gated("monitoring.server-stats", ServerStatsPage) });
const txLogsRoute = createRoute({ getParentRoute: () => shellRoute, path: "/transaction-logs", component: TransactionLogsPage });
const logsRoute = createRoute({ getParentRoute: () => shellRoute, path: "/logs", component: gated("monitoring.advanced-logs", LogsPage) });
const docsRoute = createRoute({ getParentRoute: () => shellRoute, path: "/docs", component: gated("core.docs.help", DocsPage) });
const docsSlugRoute = createRoute({ getParentRoute: () => shellRoute, path: "/docs/$slug", component: gated("core.docs.help", DocsPage) });
const testingRoute = createRoute({ getParentRoute: () => shellRoute, path: "/testing", component: TestingLayout });
const testingIndexRoute = createRoute({
  getParentRoute: () => testingRoute,
  path: "/",
  component: TestingIndexRedirect,
});
const activationTestingRoute = createRoute({ getParentRoute: () => testingRoute, path: "activation", component: gated("testing.test-activation", TestActivationPage) });
const otpPasswordTestingRoute = createRoute({ getParentRoute: () => testingRoute, path: "otp-password", component: gated("testing.test-otp-password", TestOtpPasswordPage) });
const deviceReplacementTestingRoute = createRoute({ getParentRoute: () => testingRoute, path: "device-replacement", component: gated("testing.test-device-replacement", TestDeviceReplacementPage) });
const adminTestingRoute = createRoute({ getParentRoute: () => testingRoute, path: "admin", component: gated("testing.test-admin", TestAdminPage) });
const apiToolsTestingRoute = createRoute({ getParentRoute: () => testingRoute, path: "api-tools", component: gated("testing.api-tools", ApiToolsPage) });
const soapToolsTestingRoute = createRoute({ getParentRoute: () => testingRoute, path: "soap-tools", component: gated("testing.api-tools", SoapToolsPage) });
const echannelsToolsTestingRoute = createRoute({ getParentRoute: () => testingRoute, path: "echannels-tools", component: gated("testing.api-tools", EChannelsToolsPage) });
const asymToolsTestingRoute = createRoute({ getParentRoute: () => testingRoute, path: "asym-tools", component: gated("testing.asym-tools", AsymToolsPage) });
const loadtestTestingRoute = createRoute({ getParentRoute: () => testingRoute, path: "loadtest", component: gated("testing.loadtest-generator", LoadtestGeneratorPage) });
const scenariosTestingRoute = createRoute({ getParentRoute: () => testingRoute, path: "scenarios", component: gated("testing.loadtest-generator", ScenariosPage) });

/**
 * `/testing` → první nástroj, který je v této instalaci opravdu vidět.
 * Natvrdo mířilo na API Tools; se skrytou feature to skončilo na prázdné stránce,
 * přestože položka „Testovací nástroje" v navigaci svítila kvůli jinému nástroji.
 * Musí to být komponenta, ne `beforeLoad` — viditelnost je až v React Query.
 */
function TestingIndexRedirect() {
  const features = useFeatures();
  const connectors = useConnectors();
  if (features.isLoading || connectors.isLoading) {
    return <div className="flex justify-center py-12"><LoadingIndicator /></div>;
  }
  const sections = visibleTestingSections(
    (id) => isNavVisible(visibilityOf(features.data, id)),
    (type) => (connectors.data ?? []).some((c) => c.connectorType === type),
  );
  return <Navigate to={sections[0]?.to ?? "/"} replace />;
}

const settingsRoute = createRoute({ getParentRoute: () => shellRoute, path: "/settings", component: SettingsLayout });
/** `/settings` = rozcestník (dřív redirect na Uživatele). */
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/",
  component: SettingsOverviewPage,
});
const usersSettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: "users", component: gated("admin.user-management", UsersPage) });
const connectorsSettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: "connectors", component: gated("admin.connector-config", ConnectorsPage) });
const maintenanceSettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: "maintenance", component: gated("admin.maintenance.cleanup", MaintenancePage) });
const generalSettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: "general", component: GeneralPage });
const tokenSettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: "token-settings", component: TokenSettingsPage });
const uiSettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: "ui-settings", component: UiSettingsPage });
const appearanceSettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: "appearance", component: AppearancePage });
const auditSettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: "audit", component: gated("admin.user-management", AuditPage) });
const tlsSettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: "tls", component: gated("admin.tls", TlsPage) });
const notificationsSettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: "notifications", component: gated("admin.notifications", NotificationsPage) });
const accountRoute = createRoute({ getParentRoute: () => shellRoute, path: "/account", component: AccountPage });
const apiKeysSettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: "api-keys", component: gated("admin.api-keys", ApiKeysPage) });
const licenseSettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: "license", component: LicensePage });
const directorySettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: "directory", component: gated("admin.ldap-auth", DirectoryPage) });

function ShellLayout() {
  const features = useFeatures();
  const nav = useNavVisibility();
  const show = (id: string) => isNavVisible(visibilityOf(features.data, id));

  // Nav: feature-gated (default-deny) + nav-item toggle (default show).
  const items: NavItemType[] = [{ label: "Přehled", href: "/", icon: Home02 }];
  if (show("core.token.list")) items.push({ label: "Tokeny", href: "/tokens", icon: Coins01 });
  if (navVisible(nav.data, "paatProfiles")) items.push({ label: "PAAT profily", href: "/paat", icon: Phone01 });
  if (show("monitoring.server-stats")) {
    items.push({ label: "Statistiky serveru", href: "/server-stats", icon: BarChartSquare02 });
  }
  if (show("monitoring.advanced-logs")) {
    items.push({ label: "Logy", href: "/logs", icon: FileSearch02 });
  }
  if (navVisible(nav.data, "transactionLogs")) {
    items.push({ label: "Transakční logy", href: "/transaction-logs", icon: ClockRewind });
  }
  if (navVisible(nav.data, "health")) {
    // Externí odkaz na raw actuator health (nový tab) — absolutní URL, aby ho
    // nav vyhodnotil jako external a neroutoval přes SPA basepath.
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    items.push({ label: "Health", href: `${origin}/actuator/health`, icon: Activity });
  }
  if (TESTING_FEATURES.some((f) => show(f))) {
    items.push({ label: "Testovací nástroje", href: "/testing", icon: Beaker02 });
  }
  if (show("admin.user-management")) {
    items.push({ label: "Nastavení", href: "/settings", icon: Settings01 });
  }
  if (show("core.docs.help")) {
    items.push({ label: "Nápověda", href: "/docs", icon: BookOpen01 });
  }
  items.push({ label: "Účet", href: "/account", icon: User01 });

  return (
    <AppShell items={items}>
      <Outlet />
    </AppShell>
  );
}

const routeTree = rootRoute.addChildren([
  loginRoute,
  shellRoute.addChildren([
    dashboardRoute,
    tokensRoute,
    tokenDetailRoute,
    accountRoute,
    paatRoute,
    serverStatsRoute,
    logsRoute,
    txLogsRoute,
    docsRoute,
    docsSlugRoute,
    testingRoute.addChildren([
      testingIndexRoute,
      activationTestingRoute,
      otpPasswordTestingRoute,
      deviceReplacementTestingRoute,
      adminTestingRoute,
      apiToolsTestingRoute,
      soapToolsTestingRoute,
      echannelsToolsTestingRoute,
      asymToolsTestingRoute,
      loadtestTestingRoute,
      scenariosTestingRoute,
    ]),
    settingsRoute.addChildren([
      settingsIndexRoute,
      usersSettingsRoute,
      apiKeysSettingsRoute,
      connectorsSettingsRoute,
      maintenanceSettingsRoute,
      generalSettingsRoute,
      tokenSettingsRoute,
      uiSettingsRoute,
      appearanceSettingsRoute,
      auditSettingsRoute,
      tlsSettingsRoute,
      notificationsSettingsRoute,
      directorySettingsRoute,
      licenseSettingsRoute,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  basepath: "/app",
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
